import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { Modal, ModalBody, Button } from "reactstrap";
import authUserRoleService from "services/AuthUserRoleService";
import Select from "react-select";
import companyService from "services/CompanyService";
import Swal from "sweetalert2";
import { SendaCustomSwal } from "components/Common/SendaCustomSwal";
import { FloppyDisk, Plus, SignOut, Trash } from "@phosphor-icons/react";
import { Tooltip } from "@mui/material";

const SetUserPerfilModal = ({
	show,
	onChangeClick,
	onCloseClick,
	nomePerfil,
	permissions,
}) => {
	const defaultColor = "#EFF2F7";
	const errasedColor = "#FFE5E5";
	const [dataUserRole, setDataUserRole] = useState([]);
	const [empresas, setEmpresas] = useState([]);

	const options = permissions?.map((permission) => ({
		value: Number(permission.id),
		label: permission.name,
	}));

	async function getEmpresas() {
		try {
			const response = await companyService.get();
			const empresas = response.map((item) => ({
				value: item.id,
				label: item.name,
			}));
			if (empresas.length === 1) {
				localStorage.setItem("company_id", empresas[0]?.value);
			}
			setEmpresas(empresas);
		} catch (error) {
			console.log(error);
		}
	}

	useEffect(() => {
		getEmpresas();
	}, []);

	const getUserRole = async () => {
		try {
			const response = await authUserRoleService.getById(nomePerfil?.id);

			// Mapeia os dados e remove aqueles sem company_id
			const responseData = response
				.filter((data) => data.company !== null) // Filtra registros válidos
				.map((data) => ({
					data,
					color: defaultColor,
				}));

			// Ordena os dados pelo company_id
			responseData.sort((a, b) => a.data.company_id - b.data.company_id);

			// Define o estado com os registros filtrados
			setDataUserRole(responseData);
		} catch (error) {
			console.log(error);
		}
	};

	const changeBackGroundColor = (event) => {
		SendaCustomSwal.fire({
			title: "Tem certeza?",
			text: "Tem certeza? Deseja excluir esta permissão? Esta ação não poderá ser desfeita a menos que você clique em cancelar ou em fechar na próxima confirmação.",
			iconHtml: '<i class="warning-icon"></i>',
			showCancelButton: true,
			confirmButtonText: "Sim, excluir",
			cancelButtonText: "Cancelar",
			customClass: {
				confirmButton: "custom-confirm-button",
				cancelButton: "custom-cancel-button",
			},
		}).then((result) => {
			if (result.isConfirmed) {
				const allData = [...dataUserRole];
				const countAllData = allData.filter(
					(aData) => aData.color === defaultColor,
				);
				
				// Se é um item novo (sem ID), remove diretamente da lista
				if (!event.data.id) {
					const filteredData = allData.filter(
						(dt) => dt !== event
					);
					setDataUserRole(filteredData);
					return;
				}
				
				// Se é um item existente, marca para exclusão
				const itemIndex = allData.findIndex(
					(dt) =>
						dt === event ||
						(dt?.data?.id && dt?.data?.id === event?.data?.id),
				);
				const newData = allData[itemIndex];

				if (
					newData &&
					(countAllData.length > 1 || newData.color == errasedColor)
				) {
					newData.color =
						newData.color === errasedColor ? defaultColor : errasedColor;
					setDataUserRole(allData);
				}
			}
		});
	};

	const onChangeInSelect = (company, selected) => {
		const allDataSelect = [...dataUserRole];
		const newDataSelect = allDataSelect.find(
			(dt) => dt.data.company.id === company.id,
		);
		newDataSelect.data.permission.id = selected.value;
		newDataSelect.data.permission.name = selected.label;
		setDataUserRole(allDataSelect);
	};

	const onChangeCompany = (data, selected) => {
		const allDataSelect = [...dataUserRole];
		const newDataSelect = allDataSelect.find((dt) => dt === data);
		newDataSelect.data.company.id = selected.value;
		newDataSelect.data.company.name = selected.label;
		setDataUserRole(allDataSelect);
	};

	useEffect(() => {
		if (nomePerfil) {
			getUserRole();
		}
	}, [show]);

	const customStyles = {
		option: (provided, state) => ({
			...provided,
			color: state.isSelected ? "blue" : "black",
			backgroundColor: "white",
			"&:hover": {
				backgroundColor: "#556ee6",
			},
		}),
	};

	const onChange = () => {
		SendaCustomSwal.fire({
			title: "Confirmação",
			text: "Deseja confirmar todas as alterações feitas?",
			iconHtml: '<i class="question-icon"></i>',
			showCancelButton: true,
			confirmButtonText: "Sim, confirmar",
			cancelButtonText: "Cancelar",
			customClass: {
				cancelButton: "custom-cancel-button",
				confirmButton: "custom-confirm-button",
			},
		}).then(async (result) => {
			if (result.isConfirmed) {
				const data = dataUserRole
					.filter((errased) => errased.color !== errasedColor)
					.map((final) => ({
						permission_id: String(final.data.permission.id),
						user_id: final.data.user.id,
						company_id: final.data.company.id,
						type: final.data.type,
					}));
				try {
					await onChangeClick(data);
					onCloseClick();
					getUserRole();
					SendaCustomSwal.fire({
						title: "Sucesso",
						text: "Alterações confirmadas com sucesso!",
						iconHtml: '<i class="success-icon"></i>',
						customClass: {
							confirmButton: "custom-confirm-button",
						},
					}).then(() => {
						// Recarrega a página após o usuário fechar o swal
						window.location.reload();
					});
				} catch (error) {
					SendaCustomSwal.fire({
						title: "Erro",
						text: "Não foi possível salvar as alterações. Tente novamente.",
						iconHtml: '<i class="warning-icon"></i>',
						customClass: {
							confirmButton: "custom-confirm-button",
						},
					});
					console.log(error);
				}
			}
		});
	};

	const handleAddPerfil = () => {
		const allDataSelect = [...dataUserRole];
		const info = {
			color: defaultColor,
			data: {
				permission_id: null,
				user_id: nomePerfil?.id,
				company_id: null,
				type: "0",
				user: { id: nomePerfil?.id, name: nomePerfil?.name },
				permission: {},
				company: {},
			},
		};
		allDataSelect.push(info);
		setDataUserRole(allDataSelect);
	};

	return (
		<Modal
			style={{ width: "80vw", minWidth: "50vw" }}
			isOpen={show}
			toggle={onCloseClick}
			centered={true}
		>
			<div className="modal-content">
				<ModalBody className="px-4 py-5">
					<h4
						style={{
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
							marginBottom: "20px",
						}}
					>
						Perfil por empresa
					</h4>
					<div
						style={{
							display: "flex",
							alignItems: "center",
							justifyContent: "flex-end",
							marginBottom: "10px",
						}}
					>
						<Tooltip title="Adicionar perfil" placement="top">
							{/* Colocando o button dentro do span para aparecer o tootlip */}
							<span>
								<Button
									size="sm"
									onClick={handleAddPerfil}
									style={{
										background: "none",
										padding: "10px",
										cursor: "pointer",
										color: "black",
										gap: "2px",
									}}
								>
									<Plus size={16} />
								</Button>
							</span>
						</Tooltip>
					</div>
					{options &&
						dataUserRole &&
						dataUserRole.map((dataUser, index) => {
							const empresaVisivel = dataUser?.data?.company?.id
								? empresas.find(
										(option) =>
											option.value ==
											dataUser.data.company.id,
								  )
								: null;

							// Não renderiza se a empresa não estiver visível
							if (dataUser?.data?.company?.id && !empresaVisivel)
								return null;

							// Não renderiza se a linha estiver marcada para exclusão
							if (dataUser.color === errasedColor)
								return null;

							return (
								<div
									key={index}
									style={{
										display: "flex",
										alignItems: "center",
										justifyContent: "space-between",
										borderBottom: "1px solid #ddd",
										padding: "10px 0",
										marginBottom: "10px",
									}}
								>
									<div
										style={{ flex: 1, marginRight: "10px" }}
									>
										<Select
											className="select2"
											styles={customStyles}
											placeholder="Selecione a empresa"
											onChange={(selectedOption) =>
												onChangeCompany(
													dataUser,
													selectedOption,
												)
											}
											value={empresas.find(
												(option) =>
													option.value ==
													dataUser.data.company.id,
											)}
											options={empresas}
										/>
									</div>
									{dataUser.data.company.id && (
										<div
											style={{
												flex: 1,
												marginRight: "10px",
											}}
										>
											<Select
												className="select2"
												styles={customStyles}
												placeholder="Selecione a permissão"
												onChange={(selectedOption) => {
													onChangeInSelect(
														dataUser.data.company,
														selectedOption,
													);
												}}
												value={options.find(
													(option) =>
														option.value ==
														dataUser.data.permission
															.id,
												)}
												options={options}
											/>
										</div>
									)}
									<div
										style={{
											flex: "0 0 auto",
											textAlign: "center",
											width: "40px", // Largura fixa para garantir consistência
											cursor:
												dataUserRole.length === 1
													? "not-allowed"
													: "pointer", // Aplicando o cursor aqui
											opacity:
												dataUserRole.length === 1
													? 0.5
													: 1, // Diminui a opacidade se estiver desabilitado
										}}
									>
										{dataUser.data.id ? (
											<Button
												size="sm"
												onClick={() =>
													changeBackGroundColor(
														dataUser,
													)
												}
												disabled={
													dataUserRole.length === 1
												}
												style={{
													background: "none",
													padding: "10px",
													color: "black",
												}}
											>
												<Trash size={16} />
											</Button>
										) : (
											// Placeholder para manter o espaço do botão
											<div
												style={{ height: "10px" }}
											></div>
										)}
									</div>
								</div>
							);
						})}

					<div className="hstack gap-2 justify-content-end">
						{dataUserRole.length > 0 && (
							<Button
								type="button"
								style={{
									background: "none",
									padding: 10,
									cursor: "pointer",
									color: "black",
									marginLeft: 10,
									display: "flex",
									alignItems: "center",
									justifyContent: "center",
									gap: 2,
								}}
								onClick={onChange}
							>
								<FloppyDisk size={16} /> Salvar
							</Button>
						)}
						<Button
							type="button"
							onClick={onCloseClick}
							style={{
								background: "none",
								padding: 10,
								cursor: "pointer",
								color: "black",
								display: "flex",
								alignItems: "center",
								justifyContent: "center",
								gap: 2,
							}}
						>
							<SignOut size={16} /> Fechar
						</Button>
					</div>
				</ModalBody>
			</div>
		</Modal>
	);
};

SetUserPerfilModal.propTypes = {
	preGlobalFilteredRows: PropTypes.any,
};

export default SetUserPerfilModal;
